import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Animated,
  StyleSheet,
  View,
  ViewProps,
  Modal // Import Modal
} from 'react-native';
import { Button, Divider, Layout, Text } from '@ui-kitten/components';
import * as DocumentPicker from 'expo-document-picker';
import * as Progress from 'react-native-progress';
import DoneModal from './review/DoneModal';
import { ErrorMessage } from 'src/components/shared/ErrorMessage';
import ModalHeader from 'src/components/profileMeMarker/ModalHeader';
import { NavigationProp } from '@react-navigation/native';
import { UIHelper as uh } from '../../../core';
import { uploadUserFile } from 'src/api/storage/S3Storage';
import { useAnalytics } from 'src/core/hooks/useAnalytics';

interface UploadBloodworkModalProps extends ViewProps {
  viewAssessmentHandler: (assessmentId: string) => void;
  btnBackHandler: () => void;
  navigation: NavigationProp<ReactNavigation.RootParamList>;
}

const UploadBloodworkModal = (props: UploadBloodworkModalProps) => {
  const { addAnalyticsLog } = useAnalytics('UploadBloodworkModal.tsx');

  const [error, setError] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [tooLargeError, setTooLargeError] = useState<boolean>(false);
  const moveAnim = useRef(new Animated.Value(uh.height())).current;
  const [isDoneModalVisible, setIsDoneModalVisible] = useState(false); // State for DoneModal

  const requiredSize = 5; // max pdf file size in MB
  const tooLargeMessage = `This file is too large. Please pick a file with the size less than ${requiredSize}MB`;
  const doneDes =
    "We've received your document, and we have already started working on it.\n\nIn a minute or two, it will appear for validation at the top of your Reports screen.\n";

  const styles = StyleSheet.create({
    screenContainer: {
      padding: uh.h2DP(16),
      paddingTop: 0,
      minHeight: uh.height() - uh.topPos()
    },
    animatedViewContainer: {
      height: uh.height() - uh.topPos(),
      position: 'absolute',
      alignSelf: 'center',
      width: '100%',
      transform: [{ translateY: moveAnim as any }]
    },
    container: {
      flex: 1,
      padding: uh.h2DP(16)
    },
    progressBar: {
      marginBottom: uh.h2DP(20),
      width: uh.currentViewPort() - uh.w2DP(64),
      marginLeft: uh.w2DP(16)
    },
    progressBarTitle: {
      margin: uh.h2DP(20)
    },
    button: {
      alignItems: 'center',
      padding: uh.h2DP(10)
    },
    item: {
      marginTop: uh.w2DP(8)
    },
    divider: {
      marginTop: uh.w2DP(6)
    },
    backdropStyle: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
    modalContainer: {
      // Add this style for the modal content
      backgroundColor: 'white',
      padding: uh.h2DP(16),
      borderRadius: 10,
      minWidth: uh.w2DP(300), // Ensure modal has a minimum width
      // elevation: 5, // Add shadow for Android - removed for RN 0.73
      shadowColor: '#000', // Add shadow for iOS
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 3.84
    },
    modalOverlay: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.5)' // Semi-transparent background
    }
  });

  const moveDown = (callBackFnc: () => void) => {
    Animated.timing(moveAnim, {
      toValue: uh.height(),
      duration: 500,
      useNativeDriver: true
    }).start(() => {
      callBackFnc();
    });
  };

  const handleDoneModalDone = () => {
    setIsDoneModalVisible(false);
    moveDown(props.btnBackHandler);
  };

  const handleGoToReports = () => {
    setIsDoneModalVisible(false);
    moveDown(props.btnBackHandler);
    props.navigation.navigate('Profile', { screen: 'Assessments' });
  };

  // select Bloodwork document
  const pickPdfDocument = async () => {
    const result: any = await DocumentPicker.getDocumentAsync({
      type: 'application/pdf'
    });

    if (result.type !== 'cancel') {
      // calculate file size in MB
      if (result.size && Number(Math.floor(result.size / Math.pow(1024, 2)).toFixed(2)) > requiredSize) {
        setTooLargeError(true);
        return;
      }

      setTooLargeError(false);
      setUploadProgress(0);
      setUploading(true);
      try {
        const response = await uploadUserFile(result.name, result.uri);
        if (response.status >= 200 && response.status <= 399) {
          setUploadProgress(100);
          setTimeout(() => {
            setUploading(false);
          }, 300);
          setIsDoneModalVisible(true); // Show DoneModal
        } else {
          setError("Can't upload Lab Data document.");
          addAnalyticsLog({
            function: 'pickPdfDocument',
            data: response,
            logType: 'error'
          });
          setUploading(false);
        }
      } catch (err) {
        addAnalyticsLog({
          function: 'pickPdfDocument',
          data: err,
          logType: 'error'
        });
        setError('An error occurred during upload'); // set error
        setUploading(false);
      }
    }
  };

  const moveUp = useCallback(() => {
    Animated.timing(moveAnim, {
      toValue: uh.topPos(),
      duration: 500,
      useNativeDriver: true
    }).start();
  }, [moveAnim]);

  useEffect(() => {
    moveUp();
  }, [moveUp]);

  const UploadButton = () => {
    return (
      <View style={styles.button}>
        <Button onPress={() => pickPdfDocument()}>Select File</Button>
      </View>
    );
  };

  if (uploading) {
    return (
      <Animated.View style={styles.animatedViewContainer}>
        <ModalHeader
          caption="Details"
          btnClickHandler={() => {
            moveDown(props.btnBackHandler);
          }}
        />
        <Layout style={styles.container}>
          <ErrorMessage message={error} />
          <UploadButton />
          <Text style={styles.progressBarTitle}>Uploading ...</Text>
          <Progress.Bar
            progress={uploadProgress / 100} // Progress.Bar expects value between 0 and 1
            style={styles.progressBar}
            width={uh.currentViewPort()}
          />
          <Divider />
        </Layout>
      </Animated.View>
    );
  }

  return (
    <Animated.View style={styles.animatedViewContainer}>
      <ModalHeader
        caption="Upload Lab Data Document"
        btnClickHandler={() => {
          moveDown(props.btnBackHandler);
        }}
        showFileSizeLimit={true}
      />
      <Layout style={styles.container}>
        {tooLargeError && <ErrorMessage message={tooLargeMessage} />}
        <UploadButton />
        <Divider style={styles.divider} />
      </Layout>

      {/* Done Modal */}
      <Modal visible={isDoneModalVisible} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <DoneModal
              caption="Lab Data successfully uploaded"
              description={doneDes}
              btnDoneClick={handleDoneModalDone}
              btnHyperlinkText={'Go to reports'}
              btnHyperlinkClick={handleGoToReports}
            />
          </View>
        </View>
      </Modal>
    </Animated.View>
  );
};

export default UploadBloodworkModal;
